import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import type { GenericField } from '@Types';
import type { Field, FieldInitialSettings, FieldSchema } from '@FormModule';
import { API_URL } from '@Constants';
import { convertToTitleCase } from '@Helpers';
import { useAnimalProfileStore, useSessionStore } from '@Stores';
import { useADialog } from '@Atoms';
import { ROLES } from '@AuthModule';
import { useForm, FIELDS_COMPONENTS } from '@FormModule';

const { SYSTEM_MODALS } = useADialog();

export default function () {
  const { can } = useSessionStore();
  const { pageData } = storeToRefs(useAnimalProfileStore());
  const { shelterProfile } = storeToRefs(useSessionStore());

  const { getDetailsSchema } = useForm();

  const outcome = computed(() => pageData.value.admissions[0].outcome);
  const showRtoContracts = computed(() => shelterProfile.value?.rto_contract);
  const showAdoptionContracts = computed(
    () => shelterProfile.value?.adoption_contract || shelterProfile.value?.adoptions
  );
  const fieldsSettings = computed(
    (): FieldInitialSettings => ({
      create_date: {
        settings: {
          editable: can(ROLES.EDIT_ANIMAL),
        },
      },
      outcome_type: {
        settings: {
          editable: false,
        },
      },
      outcome_source_partner: {
        render: {
          component: FIELDS_COMPONENTS.LINK,
        },
        settings: {
          editable: false,
          href: `/shelter/partner/profile/${outcome.value?.outcome_sourceId?.$oid}`,
          show: outcome.value?.outcome_source === 'partner' && can(ROLES.PARTNERS),
        },
      },
      outcome_source_guardian: {
        render: {
          component: FIELDS_COMPONENTS.LINK,
        },
        settings: {
          editable: false,
          href: `/shelter/people/profile/${outcome.value?.outcome_sourceId?.$oid}`,
          show:
            (can(ROLES.PEOPLE) || can(ROLES.ADOPTER) || can(ROLES.OWNER)) && outcome.value?.outcome_source === 'people',
        },
      },
      outcome_source_partner_transfer_out: {
        render: {
          component: FIELDS_COMPONENTS.OPEN_MODAL,
        },
        settings: {
          activeModal: SYSTEM_MODALS.ADD_PARTNER_TO_ANIMAL,
          editable: false,
          modalProps: {
            associateType: 'outcome',
          },
          show:
            (can(ROLES.PARTNERS) || can(ROLES.ADOPTER) || can(ROLES.OWNER)) &&
            can(ROLES.OUTCOME_ANIMAL) &&
            !outcome.value?.outcome_source &&
            outcome.value?.outcome_type === 'transfer_out',
        },
      },
      outcome_source_parent: {
        render: {
          component: FIELDS_COMPONENTS.OPEN_MODAL,
        },
        settings: {
          activeModal: SYSTEM_MODALS.ADD_PEOPLE_TO_ANIMAL,
          editable: false,
          modalProps: {
            associateType: 'outcome',
          },
          show:
            (can(ROLES.PEOPLE) || can(ROLES.ADOPTER) || can(ROLES.OWNER)) &&
            can(ROLES.OUTCOME_ANIMAL) &&
            !outcome.value?.outcome_source &&
            outcome.value?.outcome_type !== 'transfer_out',
        },
      },
      adoption_contract: {
        render: {
          component: FIELDS_COMPONENTS.LINK,
        },
        settings: {
          editable: false,
          href: outcome.value?.contract_pdf
            ? getPath(
                `${shelterProfile.value._id.$oid}/${outcome.value?.animal_id?.$oid}/outcome/${outcome.value?._id?.$oid}/${outcome.value?.contract_pdf?.filename}`
              )
            : `${API_URL}/file/outcome/print-adoption-contract/${outcome.value?.animal_id?.$oid}/${outcome.value?._id?.$oid}`,
          notRouterLink: true,
          show:
            outcome.value?.outcome_type === 'adopted' &&
            outcome.value?.cart_id &&
            showAdoptionContracts.value &&
            (can(ROLES.PEOPLE) || can(ROLES.ADOPTER) || can(ROLES.OWNER)),
        },
      },
      rto_contract: {
        render: {
          component: FIELDS_COMPONENTS.LINK,
        },
        settings: {
          editable: false,
          href: outcome.value?.contract_pdf
            ? getPath(
                `${shelterProfile.value._id.$oid}/${outcome.value?.animal_id?.$oid}/outcome/${outcome.value?._id?.$oid}/${outcome.value?.contract_pdf?.filename}`
              )
            : `${API_URL}/file/outcome/print-adoption-contract/${outcome.value?.animal_id?.$oid}/${outcome.value?._id?.$oid}`,
          notRouterLink: true,
          show:
            outcome.value?.outcome_type === 'return_to_owner' &&
            outcome.value?.cart_id &&
            showRtoContracts.value &&
            (can(ROLES.PEOPLE) || can(ROLES.ADOPTER) || can(ROLES.OWNER)),
        },
      },
      receipt: {
        render: {
          component: FIELDS_COMPONENTS.LINK,
        },
        settings: {
          editable: false,
          href: outcome.value?.receipt_pdf
            ? getPath(
                `${shelterProfile.value._id.$oid}/${outcome.value?.animal_id?.$oid}/outcome/${outcome.value?._id?.$oid}/${outcome.value?.receipt_pdf?.filename}`
              )
            : `${API_URL}/file/outcome/print-receipt/${outcome.value?.animal_id?.$oid}/${outcome.value?._id?.$oid}`,
          notRouterLink: true,
          show:
            (outcome.value?.outcome_type === 'adopted' || outcome.value?.outcome_type === 'return_to_owner') &&
            outcome.value?.cart_id &&
            (can(ROLES.PEOPLE) || can(ROLES.ADOPTER) || can(ROLES.OWNER)),
        },
      },
      location: {
        render: {
          component:
            can(ROLES.EDIT_ANIMAL) || can(ROLES.OUTCOME_ANIMAL) ? FIELDS_COMPONENTS.GMAP : FIELDS_COMPONENTS.TEXT,
        },
        settings: {
          activeModal: SYSTEM_MODALS.ANIMAL_ADD_LOCATION,
          activeDeleteModal: SYSTEM_MODALS.DELETE_LOCATION,
          modalProps: {
            id: outcome?.value?._id?.$oid,
          },
          address: outcome.value['google-address']?.formatted_address,
          coordinates: outcome?.value?.location?.coordinates,
          editLocation: true,
          editable: false,
          show: outcome?.value?.location || false,
          type: 'outcomes',
        },
      },
    })
  );

  const rawFields = ref([
    {
      label: 'Outcome Date',
      name: 'outcome_date',
      required: false,
      type: 'date',
      value: '',
    },
    {
      label: 'Outcome Type',
      name: 'outcome_type',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Guardian',
      name: 'outcome_source_guardian',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Transfer Partner',
      name: 'outcome_source_partner',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Transfer Partner',
      name: 'outcome_source_partner_transfer_out',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Parent',
      name: 'outcome_source_parent',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Contract',
      name: 'adoption_contract',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Contract',
      name: 'rto_contract',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Receipt',
      name: 'receipt',
      required: false,
      type: 'text',
      value: '',
    },
    {
      label: 'Location',
      name: 'location',
      required: false,
      type: 'location',
      value: '',
    },
  ]);
  const fieldsSchema = ref<FieldSchema[]>([]);
  const currentSchemaModels = ref<Set<GenericField>>(new Set());

  function positionStaticFields(fields: any) {
    const locationFieldIndex = fields.findIndex((field: Field) => field.name === 'location');
    if (locationFieldIndex === -1) return;

    const [locationField] = fields.splice(locationFieldIndex, 1);
    fields.push(locationField);

    return fields;
  }

  function getPath(path: string) {
    return `${API_URL}/shelter/files/get-file/${shelterProfile.value?._id.$oid}?file=${path}`;
  }

  function setForm() {
    fieldsSchema.value = getSchemaModels(outcome.value);
  }

  function getSchemaModels(sourceData: any) {
    const excludedNames = [
      'id',
      'google_address',
      'google-address',
      'partner_name',
      'tags',
      'photo',
      'file',
      'outcome_type',
      'header',
      'seperator',
      'paragraph',
    ];
    const dynamicFields = outcome.value?.expanded_outcome_form?.fields.filter(
      (field: Field) => !excludedNames.some((excluded) => field.name.includes(excluded))
    );

    const fields = dynamicFields ? [...rawFields.value, ...dynamicFields] : rawFields.value;

    const reorderedField = positionStaticFields(fields);

    const schema = getDetailsSchema(reorderedField, fieldsSettings.value);

    schema.forEach((field) => {
      const match = sourceData[field.name];

      switch (field.name) {
        case 'outcome_type': {
          field.model = convertToTitleCase(match);
          field.value = convertToTitleCase(match);
          break;
        }
        case 'outcome_source_partner':
        case 'outcome_source_guardian': {
          field.model = sourceData.outcome_source_name;
          field.value = sourceData.outcome_source_name;
          break;
        }
        case 'outcome_source_partner_transfer_out': {
          field.model = 'Select or add a partner';
          field.value = 'Select or add a partner';
          break;
        }
        case 'outcome_source_parent': {
          field.model = 'Select or add a person';
          field.value = 'Select or add a person';
          break;
        }
        case 'adoption_contract': {
          field.model = 'Adoption Contract';
          field.value = 'Adoption Contract';
          break;
        }
        case 'rto_contract': {
          field.model = 'RTO Contract';
          field.value = 'RTO Contract';
          break;
        }
        case 'receipt': {
          field.model = outcome.value?.receipt_pdf ? 'Payment Receipt' : 'Receipt';
          field.value = outcome.value?.receipt_pdf ? 'Payment Receipt' : 'Receipt';
          break;
        }
        default: {
          field.model = match;
          field.value = match;
        }
      }
    });

    return schema;
  }

  function resetForm() {
    currentSchemaModels.value = new Set();
    fieldsSchema.value = [];
  }

  return {
    currentSchemaModels,
    fieldsSchema,
    rawFields,

    setForm,
    resetForm,
  };
}
